import { translate } from 'components/Translation';
import React, { useEffect, useRef, useState } from 'react';
import Charity from './subcomponents/Charity';
import { CharitiesService } from 'services/charities/charities';
import { SingleCharity } from 'utils/api/models/charities/singleCharity';
import { HeadingSBold } from 'components/Typography/Typography';
import styles from 'assets/jss/material-dashboard-pro-react/views/topUpAddOns/donationTabStyle';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { finalize } from 'rxjs/operators';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import CustomInput from 'components/CustomInput/CustomInput';
import getEventValue from 'views/TopUpAddOns/helpers/getEventValue';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IDonationsDialogProps {
	selectedDonation?: types.payment.IDonation;
	setSelectedDonation: React.Dispatch<React.SetStateAction<types.payment.IDonation | undefined>>;
}

const Donations = ({ selectedDonation, setSelectedDonation }: IDonationsDialogProps) => {
	const [charities, setCharities] = useState<SingleCharity[]>([]);
	const [selectedCharity, setSelectedCharity] = useState<SingleCharity | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [donation, setDonation] = useState<number>(0);
	const classes = useStyles();
	const isMounted = useRef(true);

	useEffect(() => {
		setLoading(true);
		CharitiesService.getCharities()
			.pipe(
				finalize(() => {
					if (isMounted.current) {
						setLoading(false);
					}
				})
			)
			.subscribe(({ organizations }) => {
				if (isMounted.current) {
					setCharities(organizations);
				}
			});
	}, []);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (selectedDonation) {
			setSelectedCharity(selectedDonation.charity);
			setDonation(selectedDonation.value);
		}
	}, [selectedDonation]);

	useEffect(() => {
		if (selectedCharity && donation > 0) {
			setSelectedDonation({
				value: donation,
				charity: selectedCharity,
			});
		}
	}, [selectedCharity, donation, setSelectedDonation]);

	return (
		<div className={classes.dialogBodyWrapper}>
			<HeadingSBold>{translate('donations.dialog.select.charity')}</HeadingSBold>
			<div className={classes.charitiesWrapper} id="charities-wrapper">
				<ConditionalRender
					show={loading}
					onTrue={() => (
						<div className={classes.loaderWrapper}>
							<CustomCircularProgress text={translate('loading')} loading transparent />
						</div>
					)}
					onFalse={() => (
						<>
							{charities.map((charity) => (
								<Charity
									key={charity.id}
									name={charity.name}
									logo={charity.logo}
									background={charity.background}
									isChecked={!!selectedCharity && selectedCharity.id === charity.id}
									check={() => setSelectedCharity(charity)}
								/>
							))}
						</>
					)}
				/>
			</div>
			<HeadingSBold>{translate('donations.dialog.select.how.much')}</HeadingSBold>
			<div className={classes.donationInput}>
				<div className={classes.donationInputWrapper}>
					<CustomInput
						id="donation-value"
						formControlProps={{ fullWidth: true }}
						inputProps={{
							value: donation,
							type: 'number',
							inputProps: { min: 0 },
							onChange: (event: types.topUp.FormEvent) => setDonation(getEventValue(event)),
						}}
						variant="outlined"
						helperText={donation < 0 && translate('donations.dialog.error')}
						error={donation < 0}
					/>
				</div>
				<HeadingSBold disableMargin>{translate('OMR')}</HeadingSBold>
			</div>
		</div>
	);
};

export default Donations;
